import { PropsWithChildren, useMemo, useState } from 'react'
import { toast } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { useAuthContext } from 'components/auth'
import { PermissionEnum } from 'api/userPermissions'
import { WorkShiftCustomField } from 'api/company'
import {
  useCreateWorkShiftCustomFieldMutation,
  useDeleteWorkShiftCustomFieldMutation,
  useUpdateWorkShiftCustomFieldMutation,
  useWorkShiftCustomFields,
} from 'queries/company'

type CustomFieldsContextState = {
  state: {
    fields: WorkShiftCustomField[]
    isLoading: boolean
    canManage: boolean
    editingField: WorkShiftCustomField | undefined
    deletingField: WorkShiftCustomField | undefined
    isFormOpen: boolean
    isSaving: boolean
    isDeleting: boolean
    saveErrorMessage: string | undefined
    deleteErrorMessage: string | undefined
  }
  actions: {
    openCreate: () => void
    openEdit: (field: WorkShiftCustomField) => void
    closeForm: () => void
    openDelete: (field: WorkShiftCustomField) => void
    closeDelete: () => void
    saveField: (key: string) => void
    deleteField: () => void
  }
}

export const [CustomFieldsContext, useCustomFieldsContext] =
  createContext<CustomFieldsContextState>({
    name: 'CustomFieldsContext',
  })

export const CustomFieldsProvider = ({ children }: PropsWithChildren) => {
  const { hasPermission } = useAuthContext()
  const canManage = hasPermission(PermissionEnum.ManageShifts)
  const { data, isLoading } = useWorkShiftCustomFields()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingField, setEditingField] = useState<
    WorkShiftCustomField | undefined
  >()
  const [deletingField, setDeletingField] = useState<
    WorkShiftCustomField | undefined
  >()

  const {
    mutate: createCustomField,
    isPending: isCreating,
    error: createError,
    reset: resetCreate,
  } = useCreateWorkShiftCustomFieldMutation({
    onSuccess: () => {
      toast.success('Tag created')
      setIsFormOpen(false)
      setEditingField(undefined)
    },
  })

  const {
    mutate: updateCustomField,
    isPending: isUpdating,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateWorkShiftCustomFieldMutation({
    onSuccess: () => {
      toast.success('Tag updated')
      setIsFormOpen(false)
      setEditingField(undefined)
    },
  })

  const {
    mutate: deleteCustomField,
    isPending: isDeleting,
    error: deleteError,
    reset: resetDelete,
  } = useDeleteWorkShiftCustomFieldMutation({
    onSuccess: () => {
      toast.success('Tag deleted')
      setDeletingField(undefined)
    },
  })

  const value = useMemo((): CustomFieldsContextState => {
    return {
      state: {
        fields: data?.workShiftCustomFields ?? [],
        isLoading,
        canManage,
        editingField,
        deletingField,
        isFormOpen,
        isSaving: isCreating || isUpdating,
        isDeleting,
        saveErrorMessage: (createError ?? updateError)?.message,
        deleteErrorMessage: deleteError?.message,
      },
      actions: {
        openCreate: () => {
          resetCreate()
          resetUpdate()
          setEditingField(undefined)
          setIsFormOpen(true)
        },
        openEdit: (field) => {
          resetCreate()
          resetUpdate()
          setEditingField(field)
          setIsFormOpen(true)
        },
        closeForm: () => {
          resetCreate()
          resetUpdate()
          setIsFormOpen(false)
          setEditingField(undefined)
        },
        openDelete: (field) => {
          resetDelete()
          setDeletingField(field)
        },
        closeDelete: () => {
          resetDelete()
          setDeletingField(undefined)
        },
        saveField: (key) => {
          if (editingField) {
            updateCustomField({ customFieldId: editingField.id, key })
            return
          }
          createCustomField({ key })
        },
        deleteField: () => {
          if (!deletingField) {
            return
          }
          deleteCustomField(deletingField.id)
        },
      },
    }
  }, [
    data?.workShiftCustomFields,
    isLoading,
    canManage,
    editingField,
    deletingField,
    isFormOpen,
    isCreating,
    isUpdating,
    isDeleting,
    createError,
    updateError,
    deleteError,
    resetCreate,
    resetUpdate,
    resetDelete,
    createCustomField,
    updateCustomField,
    deleteCustomField,
  ])

  return (
    <CustomFieldsContext.Provider value={value}>
      {children}
    </CustomFieldsContext.Provider>
  )
}
